import React, { useState, useEffect, useRef } from 'react';
import { X, Users, AlertCircle, RefreshCw, ArrowRight } from 'lucide-react';
import LoadingSpinner from '../ui/LoadingSpinner';
import { useFamilyInvitationStore } from '../../store/familyInvitationStore';
import { FamilyRole } from '../../types';
import { toast } from 'sonner';
import { getAvailableRolesToInvite } from '../../lib/permissions';
import { useAuthStore } from '../../store/authStore';
import { withTimeout, getUserFriendlyError } from '../../lib/queryUtils';

// Concise, functional role labels. Cristian's instruction: describe what
// the invitee CAN do in a few words, never "editor"/"viewer"/"owner".
const ROLE_LABELS: Record<FamilyRole, { title: string; sub: string }> = {
  owner: { title: 'Control total', sub: 'Puede gestionar el grupo y todo el contenido' },
  editor: { title: 'Puede colaborar', sub: 'Ve, oye y puede añadir contenido' },
  viewer: { title: 'Solo ver y oír', sub: 'Acceso de lectura' },
};

interface InviteMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  familyGroupId: string;
  userRole: FamilyRole;
}

const InviteMemberModal: React.FC<InviteMemberModalProps> = ({
  isOpen,
  onClose,
  familyGroupId,
  userRole,
}) => {
  const [email, setEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState<FamilyRole>('editor');
  const [loading, setLoading] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [rolesError, setRolesError] = useState<string | null>(null);
  const [availableRoles, setAvailableRoles] = useState<FamilyRole[]>([]);
  const { inviteMember } = useFamilyInvitationStore();
  const { user } = useAuthStore();
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    const currentAbortController = abortControllerRef.current;

    async function loadAvailableRoles() {
      setLoadingRoles(true);
      setRolesError(null);

      try {
        const roles = await withTimeout(
          getAvailableRolesToInvite(userRole),
          10000,
          'La carga de roles tardó demasiado tiempo'
        );

        if (currentAbortController.signal.aborted) {
          return;
        }

        setAvailableRoles(roles);

        if (roles.length > 0 && !roles.includes(selectedRole)) {
          setSelectedRole(roles[0]);
        }
      } catch (error: any) {
        if (currentAbortController.signal.aborted) {
          return;
        }

        console.error('[InviteMemberModal] Error loading roles:', error);
        setRolesError(getUserFriendlyError(error));

        setAvailableRoles(['editor', 'viewer']);
        if (!['editor', 'viewer'].includes(selectedRole)) {
          setSelectedRole('editor');
        }
      } finally {
        if (!currentAbortController.signal.aborted) {
          setLoadingRoles(false);
        }
      }
    }

    loadAvailableRoles();

    return () => {
      currentAbortController.abort();
    };
  }, [userRole, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      toast.error('Por favor, introduce una dirección de correo');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      toast.error('Por favor, introduce una dirección de correo válida');
      return;
    }

    setLoading(true);

    const result = await inviteMember(familyGroupId, email.trim(), selectedRole);

    if (result.success) {
      toast.success(`Invitación enviada a ${email}`);
      setEmail('');
      setSelectedRole('editor');
      onClose();
    } else {
      toast.error(result.error || 'Error al enviar invitación');
    }

    setLoading(false);
  };

  const handleClose = () => {
    if (!loading) {
      setEmail('');
      setSelectedRole('editor');
      setRolesError(null);
      abortControllerRef.current?.abort();
      onClose();
    }
  };

  const retryLoadRoles = () => {
    setRolesError(null);
    setLoadingRoles(true);

    getAvailableRolesToInvite(userRole)
      .then(roles => {
        setAvailableRoles(roles);
        if (roles.length > 0 && !roles.includes(selectedRole)) {
          setSelectedRole(roles[0]);
        }
      })
      .catch(error => {
        setRolesError(getUserFriendlyError(error));
        setAvailableRoles(['editor', 'viewer']);
      })
      .finally(() => {
        setLoadingRoles(false);
      });
  };

  const inputClass =
    'w-full h-10 rounded-lg border border-stone-200 bg-white px-3 text-sm text-stone-900 placeholder:text-stone-400 transition-colors focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500';

  if (!isOpen) return null;

  // Custom overlay (not the shared Modal wrapper) so the warm-panel
  // header + close button are not duplicated by Modal's own white shell
  // and X. Same backdrop-click-to-close behaviour as Modal.
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !loading) handleClose();
  };

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200" onClick={handleBackdropClick}>
      <div className="w-full max-w-md overflow-hidden rounded-2xl bg-[#FEFCFA] shadow-2xl shadow-stone-200/50 border border-stone-100 animate-in zoom-in-95 duration-200">
        {/* Warm-panel header — same pattern as Auth Variant B mobile card */}
        <div className="warm-panel relative px-5 pt-4 pb-3 border-b border-stone-100">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            aria-label="Cerrar"
            className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white/60 text-stone-500 hover:bg-white/80 transition-colors disabled:opacity-50 !min-h-0 !min-w-0"
          >
            <X size={16} />
          </button>
          <div className="flex items-center gap-3 pr-10">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-rose-100 text-rose-600">
              <Users className="h-5 w-5" />
            </div>
            <div>
              <h2 className="font-display text-xl text-stone-900 leading-tight">
                Invitar a alguien
              </h2>
              <p className="text-xs text-stone-600">A tu grupo de familia</p>
            </div>
          </div>
        </div>

        {/* Form body */}
        <form onSubmit={handleSubmit} className="px-5 py-5 space-y-5">
          <div>
            <label htmlFor="invite-email" className="block text-sm font-medium text-stone-700 mb-1.5">
              Email
            </label>
            <input
              id="invite-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@ejemplo.com"
              disabled={loading}
              className={inputClass}
              autoComplete="email"
              inputMode="email"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">
              ¿Qué podrá hacer?
            </label>

            {loadingRoles ? (
              <div className="flex items-center justify-center py-6">
                <LoadingSpinner size="sm" message="Cargando opciones..." />
              </div>
            ) : rolesError ? (
              <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-rose-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-rose-800 mb-2">{rolesError}</p>
                  <button
                    type="button"
                    onClick={retryLoadRoles}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-rose-700 hover:text-rose-800"
                  >
                    <RefreshCw className="h-3 w-3" />
                    Reintentar
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {availableRoles.map((role) => {
                  const { title, sub } = ROLE_LABELS[role];
                  const selected = selectedRole === role;
                  return (
                    <label
                      key={role}
                      className={`flex items-start gap-3 rounded-xl border-2 p-3 cursor-pointer transition-all ${
                        selected
                          ? 'border-rose-500 bg-rose-50/60'
                          : 'border-stone-200 hover:border-stone-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="role"
                        value={role}
                        checked={selected}
                        onChange={(e) => setSelectedRole(e.target.value as FamilyRole)}
                        disabled={loading}
                        className="mt-0.5 h-4 w-4 accent-rose-500 shrink-0"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-stone-900">{title}</div>
                        <div className="text-xs text-stone-500 mt-0.5">{sub}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
            <p className="mt-2 text-xs text-stone-500">
              Recibirá un email con el enlace. Caduca en 7 días.
            </p>
          </div>

          <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end sm:gap-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="h-10 px-5 rounded-full border border-stone-300 bg-white text-stone-700 text-sm font-medium hover:bg-stone-50 transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || loadingRoles || availableRoles.length === 0}
              className="h-10 px-5 rounded-full bg-rose-500 hover:bg-rose-600 text-white text-sm font-semibold flex items-center justify-center gap-1.5 shadow-lg shadow-rose-500/30 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-3.5 w-3.5 rounded-full border-2 border-white/60 border-t-white animate-spin" />
                  Enviar invitación
                </span>
              ) : (
                <>
                  Enviar invitación <ArrowRight size={15} />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InviteMemberModal;
